/**
 * App Content Utilities
 * Phase 1 Day 45 - Extract module-level helpers
 *
 * Shared utilities used by AppContent component including:
 * - Authenticated fetch wrapper with auto-logout
 * - Credential status localStorage helpers
 * - Common headers and constants
 */

import logger from "./logger";
import config from "../config";

// Common headers for API requests
export const COMMON_HEADERS = {
  "Content-Type": "application/json",
  "x-api-key": config.apiKey,
};

// Full sync debounce prevents duplicate dataRefreshed:all events
export const FULL_SYNC_DEBOUNCE_MS = 10000; // 10 seconds

// Create composite key from deviceId and credentialId for pending deletion tracking
export const makePendingKey = (deviceId, credentialId) =>
  `${deviceId}:${credentialId}`;

// Module-level references for auth functions (set by AppContent on mount)
let logoutFunction = null;
let getAccessTokenFunction = null;

/**
 * Set the auth functions used by authenticatedFetch.
 * Call this from AppContent's useEffect on mount.
 */
export function setAuthFunctions(logout, getAccessToken) {
  logoutFunction = logout;
  getAccessTokenFunction = getAccessToken;
}

/**
 * Clear auth functions on cleanup.
 * Call this from AppContent's useEffect cleanup.
 */
export function clearAuthFunctions() {
  logoutFunction = null;
  getAccessTokenFunction = null;
}

/**
 * Wrapper around fetch that handles 401/403 errors by logging out the user.
 * Also ensures tokens are refreshed before making requests.
 */
export async function authenticatedFetch(url, options = {}) {
  // Proactively refresh token if needed before making request
  if (getAccessTokenFunction) {
    try {
      await getAccessTokenFunction();
      logger.log(
        "[authenticatedFetch] Token validated/refreshed before request"
      );

      // Dispatch event to notify components of user activity (keeps session alive)
      window.dispatchEvent(new CustomEvent("userActivity"));
    } catch (err) {
      logger.warn("[authenticatedFetch] Failed to get valid token:", err);
      // Token refresh failed, let the 401 handler deal with it
    }
  }

  const response = await fetch(url, options);

  // Check for 401 Unauthorized or 403 Forbidden
  if ((response.status === 401 || response.status === 403) && logoutFunction) {
    logger.error(`[authenticatedFetch] ${response.status} error from ${url}`, {
      status: response.status,
      statusText: response.statusText,
      url: url,
      timestamp: new Date().toISOString(),
    });

    // Always logout on 401 (session expired)
    if (response.status === 401) {
      logger.warn("Session expired (401). Logging out user...");
      setTimeout(() => {
        logoutFunction("session_expired");
      }, 500);
    }
    // Only logout on 403 for auth-related endpoints, not device/credential actions
    else if (response.status === 403) {
      const isAuthEndpoint =
        url.includes("/user-profile") ||
        url.includes("/organizations") ||
        url.includes("/users");

      if (isAuthEndpoint) {
        logger.warn(
          "Access forbidden on auth endpoint (403). Logging out user..."
        );
        setTimeout(() => {
          logoutFunction("access_denied");
        }, 500);
      } else {
        logger.warn(
          `Access forbidden (403) on ${url} - permission denied but keeping session active`
        );
      }
    }
  }

  return response;
}

/**
 * Get credential statuses from localStorage
 */
export function getStatusStore() {
  try {
    const statuses = localStorage.getItem("credentialStatuses");
    return statuses ? JSON.parse(statuses) : {};
  } catch (e) {
    return {};
  }
}

/**
 * Remove a credential status from localStorage
 */
export function removeStatusFromStore(credentialId) {
  if (!credentialId) return;
  const statuses = getStatusStore();
  delete statuses[credentialId];
  localStorage.setItem("credentialStatuses", JSON.stringify(statuses));
}
